import React from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { ChevronDownIcon, ChevronUpIcon } from '../../assets';
import { breakpointMap, theme } from '../../theme';
import { Checkbox } from '../form';
import { BodyText } from '../typography';
import { BaseAccordion } from './BaseAccordion';
import { AccordionSize } from './types';

export type AccordionCheckboxProps = {
  body: string;
  open: boolean;
  size: AccordionSize;
  subtitle: string;
  title: string;
  toggleOpen: () => void;
};

type sizeStyle = {
  iconWrapper: FlattenSimpleInterpolation;
  icon: FlattenSimpleInterpolation;
  input: FlattenSimpleInterpolation;
  subtitle: FlattenSimpleInterpolation;
  titleWrapper: FlattenSimpleInterpolation;
};

const smallStyles: sizeStyle = {
  iconWrapper: css`
    flex: 0 0 40px;
  `,
  icon: css`
    width: 18px;
  `,
  input: css`
    & > .js-label {
      ${theme.typography.body.md}
      font-weight: 700;
      margin-left: ${theme.spacings[3].rem};
    }
  `,
  subtitle: css`
    ${theme.typography.body.sm}
  `,
  titleWrapper: css`
    padding: ${theme.spacings[2].rem};
  `,
};

const largeStyles: sizeStyle = {
  iconWrapper: css`
    flex: 0 0 52px;
  `,
  icon: css`
    width: 22px;
  `,
  input: css`
    & > .js-label {
      ${theme.typography.body.lg}
      font-weight: 700;
      margin-left: ${theme.spacings[4].rem};
    }
  `,
  subtitle: css`
    ${theme.typography.body.lg}
  `,
  titleWrapper: css`
    padding: ${theme.spacings[3].rem} ${theme.spacings[4].rem};
  `,
};

const accordionSizeStyles = {
  xs: smallStyles,
  sm: smallStyles,
  md: largeStyles,
  lg: largeStyles,
};

const sizeStyles = (key: keyof sizeStyle, size: AccordionSize) =>
  breakpointMap(
    size,
    (accordionSize) => accordionSizeStyles[accordionSize][key]
  );

const Accordion = styled(BaseAccordion)`
  &:first-of-type {
    border-top: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  }
`;

const Header = styled.div`
  border: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-top: none;
  display: flex;
  justify-content: space-between;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${theme.colors.neutrals.solidGrays[8]};
  }
`;

const TitleWrapper = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('titleWrapper', size)};
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Input = styled(Checkbox)<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('input', size)};
`;

const Subtitle = styled(BodyText)<{ componentSize: AccordionSize }>`
  ${({ componentSize }) => sizeStyles('subtitle', componentSize)};
  margin-left: ${theme.spacings[3].rem};
`;

const IconWrapper = styled.div<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('iconWrapper', size)};
  align-items: center;
  border-left: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  display: flex;
  justify-content: center;
`;

const IconStyles = css<{ size: AccordionSize }>`
  ${({ size }) => sizeStyles('icon', size)};
  fill: ${theme.colors.primary.graysGray};
`;

const ChevronDown = styled(ChevronDownIcon)`
  ${IconStyles}
`;

const ChevronUp = styled(ChevronUpIcon)`
  ${IconStyles}
`;

const Body = styled(BodyText)`
  border: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-top: none;
  padding: ${theme.spacings[4].rem};
`;

export const AccordionCheckbox: React.FC<AccordionCheckboxProps> = ({
  body,
  open,
  size,
  subtitle,
  title,
  toggleOpen,
}) => (
  <Accordion
    header={
      <Header>
        <TitleWrapper size={size}>
          <Input label={title} onChange={() => null} size={size} />
          <Subtitle componentSize={size}>
            <b>{subtitle}</b>
          </Subtitle>
        </TitleWrapper>
        <IconWrapper size={size}>
          {open ? <ChevronUp size={size} /> : <ChevronDown size={size} />}
        </IconWrapper>
      </Header>
    }
    onToggleOpen={toggleOpen}
    open={open}
  >
    <Body size="sm">{body}</Body>
  </Accordion>
);
